import { createAsyncThunk } from '@reduxjs/toolkit'
import { Actions } from '@state/actions'
import { PatientDto, PatientService } from '@services/api'
import { BillingService } from '@services/BillingService'
import { CouvertureDto } from 'src/common/interfaces'
import { PaginatedDto } from '@services/extendedType'
import { PatientsState } from '@state/reducers/patientsReducer'
import {
  PatientMergeHistoryDto,
  PatientServiceV2,
} from '@services/PatientService'
import {
  CreatePatientDTO,
  FindPatientCoverageQuery,
  PatientStats,
} from '@services/dtos'

export const fetchPatients = createAsyncThunk<
  PaginatedDto<PatientDto>,
  void,
  any
>(Actions.PATIENTS_FIND_ALL, async (_, { rejectWithValue, getState }) => {
  try {
    const { patientsFilter } = (getState() as { patients: PatientsState })
      .patients

    return await PatientService.findAllv2(patientsFilter)
  } catch (e: any) {
    if (e.response) {
      return rejectWithValue(e.response.data.message)
    }
    return rejectWithValue(e.response.data.message)
  }
})

export const fetchSuggestPatients = createAsyncThunk<
  PaginatedDto<PatientDto>,
  void,
  any
>(Actions.PATIENTS_SUGGEST_ALL, async (_, { rejectWithValue, getState }) => {
  try {
    const { suggestedFilters } = (getState() as { patients: PatientsState })
      .patients

    return await PatientService.suggest(suggestedFilters)
  } catch (e: any) {
    if (e.response) {
      return rejectWithValue(e.response.data.message)
    }
    return rejectWithValue(e.response.data.message)
  }
})

export const fetchOnePatientById = createAsyncThunk<
  PatientDto,
  { id: number; visitId?: number }
>(Actions.PATIENT_FIND_ONE, async (q, { rejectWithValue }) => {
  try {
    return await PatientService.findOne(q)
  } catch (e: any) {
    if (e.response) {
      return rejectWithValue(e.response.data.message)
    }
    return rejectWithValue(e.response.data.message)
  }
})

export const findInvoices = createAsyncThunk(
  Actions.PATIENT_FIND_INVOICES,
  async (id: number, { rejectWithValue }) => {
    try {
      return PatientService.findInvoices({ id })
    } catch (e: any) {
      if (e.response) {
        return rejectWithValue(e.response.data.message)
      }
      return rejectWithValue(e.response.data.message)
    }
  },
)

export const updatePatient = createAsyncThunk<PatientDto, PatientDto, any>(
  Actions.PATIENT_UPDATE,
  async (data: PatientDto, { rejectWithValue }) => {
    try {
      return await PatientService.update(data)
    } catch (e: any) {
      if (e.response) {
        return rejectWithValue(e.response.data.message)
      }
      return rejectWithValue(e.response.data.message)
    }
  },
)

export const doGetCouverture = createAsyncThunk<
  CouvertureDto,
  FindPatientCoverageQuery,
  any
>(Actions.GET_PATIENT_COVERAGE, async (q, { rejectWithValue }) => {
  try {
    const [response] = await BillingService.getCouverture(q)
    return response
  } catch (e: any) {
    if (e.response) {
      return rejectWithValue(e.response.data.message)
    }
    return rejectWithValue(e.response.data.message)
  }
})

export const doMergePatients = createAsyncThunk<
  void,
  { sourcePatientId: number; targetPatientId: number },
  any
>(Actions.MergePatients, async ({ sourcePatientId, targetPatientId }) => {
  await PatientServiceV2.mergePatient(sourcePatientId, targetPatientId)
})

export const doCancelMerge = createAsyncThunk<
  void,
  { pId: number; mergeId: number },
  any
>(Actions.CancelMerge, async ({ pId, mergeId }) => {
  await PatientServiceV2.cancelPatientMerge(pId, mergeId)
})

export const doFetchMergeHistory = createAsyncThunk<
  PatientMergeHistoryDto[],
  number
>(Actions.FETCH_MERGE_HISTORY, async (patientId) => {
  return await PatientServiceV2.findMergeHistory(patientId)
})

export const doCreatePatient = createAsyncThunk<PatientDto, CreatePatientDTO>(
  'patients/create',
  async (data) => {
    return await PatientService.create(data)
  },
)

// export const getPatientStats = createAsyncThunk<
//   { sold: number; examCount: number },
//   number
// >('patients/getStats', async (patientId) => {
//   return await PatientServiceV2.getPatientStats(patientId)
// })

// export const getPatientNoShowsApp = createAsyncThunk<
//   AppointmentMinDTO[],
//   number
// >('patients/NoshowsAppointment', async (patientId) => {
//   return await PatientServiceV2.getNoShowAppointments(patientId)
// })

export const doGetAllPatientStats = createAsyncThunk<PatientStats, number>(
  'patients/getAllStats',
  async (patientId) => {
    const [basicStats, noShows] = await Promise.all([
      PatientServiceV2.getPatientStats(patientId),
      PatientServiceV2.getNoShowAppointments(patientId),
    ])

    return {
      sold: basicStats.sold,
      examCount: basicStats.examCount,
      noShows,
      noShowsCount: noShows.length,
    }
  },
)
